import React, { useState, useEffect } from "react";
import { FaPlay, FaEye, FaSearch, FaHistory, FaUser, FaChevronDown, FaChevronUp, FaMapMarkerAlt, FaTag, FaCalendarAlt, FaUsers, FaVideo, FaClock, FaPhone, FaIdCard, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";

const RaiseVoiceHistoryInAdmin = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [previewMedia, setPreviewMedia] = useState(null);
  const [showReporterDetails, setShowReporterDetails] = useState({});
  const [expandedUsers, setExpandedUsers] = useState({});
  const [expandedAds, setExpandedAds] = useState({});

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Group expansion state
  const [expandedGroups, setExpandedGroups] = useState({});

  useEffect(() => {
    const fetchPostHistory = async () => {
      setLoading(true);
      try {
        console.log("=== FETCHING VOICE HISTORY ===");
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/admin/get/ryv/history`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        console.log("Fetched RYV History Response:", response);
        console.log("Response data:", response.data);
        console.log("History array:", response.data.history);

        const data = response.data.history || [];
        console.log("Setting posts with data:", data);
        setPosts(Array.isArray(data) ? data : [data]);
      } catch (error) {
        console.error("Error fetching voice history:", error);
        if (error.response && error.response.status === 404) {
          console.log("No history found - setting empty array");
          setPosts([]);
        } else {
          console.error("API Error:", error.response?.data);
          toast.error("Failed to load post history: " + (error.response?.data?.message || error.message));
        }
      } finally {
        setLoading(false);
      }
    };
    fetchPostHistory();
  }, []);

  // Flatten posts for table display
  const flattenPosts = (data) => {
    const flattened = [];
    data.forEach((item) => {
      const ad = item.adId || item;
      const user = ad.userId || ad;
      const reporter = item.reporterId || {};

      flattened.push({
        id: item._id,
        adId: ad._id || ad,
        adDescription: ad.description || 'N/A',
        adStatus: ad.status || 'N/A',
        adCreatedAt: ad.createdAt,
        adImage: ad.image,
        adVideo: ad.video,
        userId: user._id || user,
        userName: user.name || 'N/A',
        userPhone: user.phoneNo || 'N/A',
        userState: user.state || 'N/A',
        userCity: user.city || 'N/A',
        reporterId: reporter._id || reporter,
        reporterName: reporter.name || 'N/A',
        reporterEmail: reporter.email || 'N/A',
        reporterIinsafId: reporter.iinsafId || 'N/A',
        platform: item.platform || 'N/A',
        videoLink: item.videoLink || 'N/A',
        submittedAt: item.submittedAt,
        createdAt: item.createdAt
      });
    });
    return flattened;
  };

  // Sorting function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Sort data
  const sortData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle date sorting
      if (sortConfig.key.includes('At') || sortConfig.key.includes('Date')) {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      // Handle string sorting
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  // Filter and process posts for table
  const flattenedPosts = flattenPosts(posts);

  const filteredPosts = flattenedPosts.filter((post) => {
    const search = searchTerm.toLowerCase();
    return (
      post.adId.toString().toLowerCase().includes(search) ||
      post.adDescription.toLowerCase().includes(search) ||
      post.userName.toLowerCase().includes(search) ||
      post.userPhone.toLowerCase().includes(search) ||
      post.reporterName.toLowerCase().includes(search) ||
      post.reporterEmail.toLowerCase().includes(search) ||
      post.reporterIinsafId.toLowerCase().includes(search) ||
      post.platform.toLowerCase().includes(search) ||
      post.adStatus.toLowerCase().includes(search) ||
      (post.adCreatedAt && new Date(post.adCreatedAt).toLocaleDateString().toLowerCase().includes(search)) ||
      (post.submittedAt && new Date(post.submittedAt).toLocaleDateString().toLowerCase().includes(search))
    );
  });

  const sortedPosts = sortData(filteredPosts);

  // Group posts by Ad ID
  const groupPostsByAdId = (posts) => {
    const grouped = {};
    posts.forEach((post) => {
      const adId = post.adId.toString();
      if (!grouped[adId]) {
        grouped[adId] = {
          adId: adId,
          adData: {
            description: post.adDescription,
            status: post.adStatus,
            createdAt: post.adCreatedAt,
            image: post.adImage,
            video: post.adVideo,
            user: {
              name: post.userName,
              phone: post.userPhone,
              state: post.userState,
              city: post.userCity
            }
          },
          reporters: []
        };
      }
      grouped[adId].reporters.push({
        id: post.id,
        reporterName: post.reporterName,
        reporterIinsafId: post.reporterIinsafId,
        platform: post.platform,
        videoLink: post.videoLink,
        submittedAt: post.submittedAt,
        createdAt: post.createdAt
      });
    });
    return grouped;
  };

  const groupedPosts = groupPostsByAdId(sortedPosts);

  // Convert grouped data to array for pagination
  const groupedArray = Object.values(groupedPosts);

  // Pagination logic
  const totalPages = Math.ceil(groupedArray.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedGroups = groupedArray.slice(startIndex, endIndex);

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Reset to first page when search term changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleMediaClick = (media) => setPreviewMedia(media);
  const closePreview = () => setPreviewMedia(null);

  // Get sort icon
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="w-3 h-3 text-gray-400" />;
    return sortConfig.direction === 'asc'
      ? <FaSortUp className="w-3 h-3 text-blue-500" />
      : <FaSortDown className="w-3 h-3 text-blue-500" />;
  };

  // Toggle group expansion
  const toggleGroupExpansion = (adId) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [adId]: !prev[adId],
    }));
  };

  return (
    <div className="p-6">
      {previewMedia && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={closePreview}
        >
          <div
            className="max-w-4xl max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {previewMedia.type === "image" ? (
              <img
                src={previewMedia.url}
                alt="Preview"
                className="max-w-full max-h-[80vh] object-contain"
              />
            ) : (
              <video
                src={previewMedia.url}
                controls
                autoPlay
                className="max-w-full max-h-[80vh]"
              />
            )}
            <button
              onClick={closePreview}
              className="mt-4 bg-white text-gray-800 px-4 py-2 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={3000} />

      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold text-gray-800">
          Raise Your Voice History
        </h1>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative">
            <input
              type="text"
              placeholder="Search posts..."
              className="pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          </div>
        </div>
      </div>

      {/* Stats Card */}
      <div className="mb-8">
        <div className="bg-purple-500 p-6 rounded-xl shadow-lg text-white">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm opacity-80">Total RYV Groups</p>
              <p className="text-2xl font-bold">{groupedArray.length}</p>
            </div>
            <FaHistory className="w-10 h-10 opacity-80" />
          </div>
        </div>
      </div>

      {/* Comprehensive Table */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
        </div>
      ) : paginatedGroups.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
          <FaHistory className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-xl font-semibold text-gray-700">
            No History Found
          </h3>
          <p className="text-gray-500">
            {searchTerm
              ? "No posts match your search criteria."
              : "You don't have any post history at the moment."}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full table-fixed">
              <thead className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                <tr>
                  <th className="w-16 px-3 py-4 text-left text-xs font-medium uppercase tracking-wider">
                    #
                  </th>
                  <th className="w-48 px-3 py-4 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-blue-700 transition-colors" onClick={() => handleSort('adId')}>
                    <div className="flex items-center gap-1">
                      RYV ID
                      {getSortIcon('adId')}
                    </div>
                  </th>
                  <th className="w-48 px-3 py-4 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-blue-700 transition-colors" onClick={() => handleSort('adDescription')}>
                    <div className="flex items-center gap-1">
                      Description
                      {getSortIcon('adDescription')}
                    </div>
                  </th>
                  <th className="w-24 px-3 py-4 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-blue-700 transition-colors" onClick={() => handleSort('adStatus')}>
                    <div className="flex items-center gap-1">
                      Status
                      {getSortIcon('adStatus')}
                    </div>
                  </th>
                  <th className="w-32 px-3 py-4 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-blue-700 transition-colors" onClick={() => handleSort('userName')}>
                    <div className="flex items-center gap-1">
                      User
                      {getSortIcon('userName')}
                    </div>
                  </th>
                  <th className="w-32 px-3 py-4 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-blue-700 transition-colors" onClick={() => handleSort('userPhone')}>
                    <div className="flex items-center gap-1">
                      Phone
                      {getSortIcon('userPhone')}
                    </div>
                  </th>
                  <th className="w-40 px-3 py-4 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-blue-700 transition-colors" onClick={() => handleSort('userState')}>
                    <div className="flex items-center gap-1">
                      Location
                      {getSortIcon('userState')}
                    </div>
                  </th>
                  <th className="w-32 px-3 py-4 text-left text-xs font-medium uppercase tracking-wider">
                    Reporters
                  </th>
                  <th className="w-32 px-3 py-4 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-blue-700 transition-colors" onClick={() => handleSort('adCreatedAt')}>
                    <div className="flex items-center gap-1">
                      Ad Created
                      {getSortIcon('adCreatedAt')}
                    </div>
                  </th>
                  <th className="w-24 px-3 py-4 text-left text-xs font-medium uppercase tracking-wider">
                    Media
                  </th>
                  <th className="w-24 px-3 py-4 text-left text-xs font-medium uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedGroups.map((group, index) => {
                  const isExpanded = expandedGroups[group.adId];
                  const reporterCount = group.reporters.length;

                  return (
                    <React.Fragment key={group.adId}>
                      {/* Main Group Row */}
                      <tr className="hover:bg-gray-50 transition-colors bg-blue-50">
                        <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {startIndex + index + 1}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900 font-mono font-bold">
                          {group.adId}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-900">
                          <div className="max-w-xs truncate" title={group.adData.description}>
                            {group.adData.description}
                          </div>
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${group.adData.status === 'completed' ? 'bg-green-100 text-green-800' :
                              group.adData.status === 'running' ? 'bg-blue-100 text-blue-800' :
                                group.adData.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                  'bg-gray-100 text-gray-800'
                            }`}>
                            {group.adData.status}
                          </span>
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex items-center">
                            <FaUser className="w-4 h-4 text-gray-400 mr-2" />
                            {group.adData.user.name}
                          </div>
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex items-center">
                            <FaPhone className="w-4 h-4 text-gray-400 mr-2" />
                            {group.adData.user.phone}
                          </div>
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex items-center">
                            <FaMapMarkerAlt className="w-4 h-4 text-gray-400 mr-2" />
                            {group.adData.user.state}, {group.adData.user.city}
                          </div>
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex items-center gap-2">
                            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-semibold">
                              {reporterCount} Reporter{reporterCount > 1 ? 's' : ''}
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                          {group.adData.createdAt ? new Date(group.adData.createdAt).toLocaleDateString("en-GB") : 'N/A'}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex gap-1">
                            {group.adData.image && group.adData.image.trim() !== '' && (
                              <img
                                src={`${process.env.REACT_APP_API_BASE_URL}/${group.adData.image.replace(/\\/g, '/')}`}
                                alt="ad-image"
                                className="w-8 h-8 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => handleMediaClick({ type: 'image', url: `${process.env.REACT_APP_API_BASE_URL}/${group.adData.image.replace(/\\/g, '/')}` })}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            )}
                            {group.adData.video && group.adData.video.trim() !== '' && (
                              <video
                                preload="metadata"
                                className="w-8 h-8 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => handleMediaClick({ type: 'video', url: `${process.env.REACT_APP_API_BASE_URL}/${group.adData.video.replace(/\\/g, '/')}` })}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              >
                                <source src={`${process.env.REACT_APP_API_BASE_URL}/${group.adData.video.replace(/\\/g, '/')}`} type="video/mp4" />
                              </video>
                            )}
                            {(!group.adData.image || group.adData.image.trim() === '') && (!group.adData.video || group.adData.video.trim() === '') && (
                              <span className="text-gray-400 text-xs">-</span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                          <button
                            onClick={() => toggleGroupExpansion(group.adId)}
                            className="inline-flex items-center px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white text-xs rounded transition-colors"
                          >
                            {isExpanded ? (
                              <>
                                <FaChevronUp className="w-3 h-3 mr-1" />
                                Hide
                              </>
                            ) : (
                              <>
                                <FaChevronDown className="w-3 h-3 mr-1" />
                                Show
                              </>
                            )}
                          </button>
                        </td>
                      </tr>

                      {/* Expanded Reporter Rows */}
                      {isExpanded && group.reporters.map((reporter, reporterIndex) => (
                        <tr key={reporter.id} className="bg-gray-50 hover:bg-gray-100 transition-colors">
                          <td className="px-3 py-2 text-xs text-gray-500 text-center">
                            {reporterIndex + 1}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-500 text-center">
                            Reporter
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-700">
                            <div className="flex items-center gap-2">
                              <FaUsers className="w-3 h-3 text-blue-500" />
                              <span className="font-medium">{reporter.reporterName}</span>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-500">
                            -
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-500">
                            -
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-500">
                            -
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-500">
                            -
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-700">
                            <div className="space-y-1">
                              <div className="flex items-center gap-1">
                                <span className="bg-indigo-100 text-indigo-800 px-2 py-1 rounded text-xs font-mono">
                                  {reporter.reporterIinsafId}
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                                  {reporter.platform}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-500">
                            -
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-700">
                            {reporter.videoLink && reporter.videoLink !== 'N/A' ? (
                              <a
                                href={reporter.videoLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center px-2 py-1 bg-green-500 hover:bg-green-600 text-white text-xs rounded transition-colors"
                                title={reporter.videoLink}
                              >
                                <FaPlay className="w-3 h-3 mr-1" />
                                Watch
                              </a>
                            ) : (
                              <span className="text-gray-400 text-xs">-</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-500">
                            {reporter.submittedAt ? new Date(reporter.submittedAt).toLocaleDateString("en-GB") : 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center mt-6 bg-white p-4 rounded-lg shadow-sm">
          <div className="text-sm text-gray-700">
            Showing {startIndex + 1} to {Math.min(endIndex, groupedArray.length)} of {groupedArray.length} RYV groups
          </div>
          <nav className="inline-flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-3 py-2 rounded border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors text-sm ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''
                }`}
            >
              Previous
            </button>

            {/* Page numbers */}
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`px-3 py-2 rounded text-sm transition-colors ${currentPage === pageNum
                      ? 'bg-blue-500 text-white'
                      : 'border border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                    }`}
                >
                  {pageNum}
                </button>
              );
            })}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`px-3 py-2 rounded border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors text-sm ${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''
                }`}
            >
              Next
            </button>
          </nav>
        </div>
      )}
    </div>
  );
};

export default RaiseVoiceHistoryInAdmin;
